import { useNavigate } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";

type Block =
  | { type: "p"; text: string }
  | { type: "list"; items: string[] }
  | { type: "contact"; email: string; phone: string }
  | {
      type: "address";
      lines: string[];
      email: string;
      phone: string;
    };

const SECTIONS: { title: string; blocks: Block[] }[] = [
  {
    title: "1. Purpose",
    blocks: [
      {
        type: "p",
        text: 'This Refund Policy explains when Trevica LTD ("Company", "we", "our", or "us") may issue refunds for purchases made through our website or related Services.',
      },
      {
        type: "p",
        text: "We strive to provide reliable services and a positive customer experience. Every refund request is reviewed fairly and individually in accordance with this Policy and applicable consumer protection laws.",
      },
    ],
  },
  {
    title: "2. When a Refund May Be Available",
    blocks: [
      { type: "p", text: "You may be eligible to request a refund if:" },
      {
        type: "list",
        items: [
          "Your payment was made directly to Trevica LTD through our website or an authorized payment provider.",
          "A verified technical issue significantly prevented the delivery or proper functioning of the purchased service.",
          "Your request is submitted within 14 calendar days from the date of purchase.",
        ],
      },
      { type: "p", text: "Each request is assessed individually, and approval is not automatic." },
    ],
  },
  {
    title: "3. Situations Generally Not Eligible for Refunds",
    blocks: [
      { type: "p", text: "Refunds will normally not be granted in circumstances including, but not limited to:" },
      {
        type: "list",
        items: [
          "You changed your mind after completing the purchase.",
          "The Service was available but was not used.",
          "The issue resulted from incorrect use of the Service.",
          "Problems were caused by third-party software, hardware, internet connectivity, or other factors outside our reasonable control.",
          "The refund request was submitted after the applicable refund period.",
        ],
      },
    ],
  },
  {
    title: "4. How to Submit a Refund Request",
    blocks: [
      { type: "p", text: "To request a refund, please contact our support team." },
      { type: "contact", email: "support@trevicatech.com", phone: "+44 7451 257027" },
      { type: "p", text: "To help us review your request promptly, please provide:" },
      {
        type: "list",
        items: [
          "Your full name.",
          "A contact email address.",
          "Proof of purchase (including payment date and transaction reference, where available).",
          "A clear description of the issue and the reason for your request.",
        ],
      },
      { type: "p", text: "We may request additional information if necessary to verify the claim." },
    ],
  },
  {
    title: "5. Review Procedure",
    blocks: [
      { type: "p", text: "Once all required information has been received, our team will review your request." },
      { type: "p", text: "During the review process, we may:" },
      {
        type: "list",
        items: [
          "Request additional supporting information.",
          "Verify transaction details.",
          "Determine whether the circumstances qualify under this Refund Policy.",
        ],
      },
      {
        type: "p",
        text: "We aim to complete reviews within a reasonable timeframe and will notify you of our decision by email.",
      },
    ],
  },
  {
    title: "6. Approved Refunds",
    blocks: [
      { type: "p", text: "If your refund request is approved:" },
      {
        type: "list",
        items: [
          "Funds will be returned using the original payment method whenever reasonably possible.",
          "Processing times typically range between 5 and 14 business days, depending on your payment provider or financial institution.",
          "Any applicable taxes or fees will be handled in accordance with applicable law.",
        ],
      },
    ],
  },
  {
    title: "7. Subscription Cancellations",
    blocks: [
      { type: "p", text: "If the Service includes recurring subscriptions:" },
      {
        type: "list",
        items: [
          "You may cancel your subscription before the next billing cycle to prevent future charges.",
          "Cancellation affects future renewals only.",
          "Payments already processed remain subject to this Refund Policy.",
        ],
      },
    ],
  },
  {
    title: "8. Limitation of Responsibility",
    blocks: [
      { type: "p", text: "The Company shall not be responsible for refund requests arising from:" },
      {
        type: "list",
        items: [
          "Device or software compatibility issues outside our control.",
          "Internet connectivity problems.",
          "Interruptions or failures caused by third-party providers.",
          "Circumstances beyond our reasonable control, including force majeure events.",
        ],
      },
    ],
  },
  {
    title: "9. Changes to this Refund Policy",
    blocks: [
      { type: "p", text: "We reserve the right to amend or update this Refund Policy at any time." },
      {
        type: "p",
        text: "The latest version will be published on this page and will become effective immediately upon publication unless otherwise stated.",
      },
      {
        type: "p",
        text: "Your continued use of the Services following any updates constitutes acceptance of the revised Refund Policy.",
      },
    ],
  },
  {
    title: "10. Contact Information",
    blocks: [
      { type: "p", text: "If you have any questions regarding this Refund Policy, please contact us:" },
      {
        type: "address",
        lines: ["Trevica LTD", "307 Cotton Exchange", "Old Hall Street", "Liverpool", "L3 9LQ", "United Kingdom"],
        email: "support@trevicatech.com",
        phone: "+44 7451 257027",
      },
    ],
  },
];

function SectionBlock({ block }: { block: Block }) {
  if (block.type === "p") {
    return (
      <p className="text-sm md:text-base text-ink-700 leading-relaxed mt-3">
        {block.text}
      </p>
    );
  }

  if (block.type === "list") {
    return (
      <ul className="mt-3 space-y-2 list-disc pl-5">
        {block.items.map((item, i) => (
          <li key={i} className="text-sm md:text-base text-ink-700 leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    );
  }

  if (block.type === "contact") {
    return (
      <div className="mt-3 text-sm md:text-base text-ink-700 leading-relaxed">
        <p>
          Email: <span className="font-medium text-ink-950">{block.email}</span>
        </p>
        <p>
          Phone: <span className="font-medium text-ink-950">{block.phone}</span>
        </p>
      </div>
    );
  }

  return (
    <div className="mt-3 text-sm md:text-base text-ink-700 leading-relaxed">
      <p className="font-semibold text-ink-950">{block.lines[0]}</p>
      {block.lines.slice(1).map((line, i) => (
        <p key={i}>{line}</p>
      ))}
      <p className="mt-2">
        Email: <span className="font-medium text-ink-950">{block.email}</span>
      </p>
      <p>
        Phone: <span className="font-medium text-ink-950">{block.phone}</span>
      </p>
    </div>
  );
}

export default function RefundPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream-50 font-sans text-ink-950">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full pt-28 pb-16 md:pt-36 md:pb-24 overflow-hidden bg-cream-100 border-b border-ink-950/5">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-700 hover:text-ink-950 transition-colors cursor-pointer mb-6"
            >
              <i className="ri-arrow-left-line text-base"></i>
              Back
            </button>
            <p className="text-xs md:text-sm font-medium uppercase tracking-[0.2em] text-ink-500 mb-4">
              Last updated: July 2026
            </p>
            <h1 className="font-display font-bold text-ink-950 leading-[1.08] tracking-tight text-3xl md:text-4xl lg:text-5xl">
              Refund Policy
            </h1>
            <p className="mt-4 text-sm md:text-base text-ink-700 leading-relaxed max-w-2xl">
              We stand behind the quality of our work. This policy explains how refunds are handled for our development, consulting, and marketing services.
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="relative w-full py-16 md:py-24 bg-cream-50">
          <div className="max-w-4xl mx-auto px-6 md:px-10 lg:px-14">
            <div className="space-y-10 md:space-y-14">
              {SECTIONS.map((section) => (
                <div key={section.title}>
                  <h2 className="font-display font-bold text-xl md:text-2xl text-ink-950 mb-4">
                    {section.title}
                  </h2>
                  {section.blocks.map((block, i) => (
                    <SectionBlock key={i} block={block} />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
